/**
 * Centralized color utilities for design system components
 */

// Primary color fallback
export const PRIMARY_COLOR_FALLBACK = 'indigo';

/**
 * Allowed Radix UI theme colors
 */
export const ALLOWED_COLORS = [
  'gray',
  'blue',
  'red',
  'green',
  'teal',
  'ruby',
  'amber',
  'orange',
  'crimson',
  'jade',
  'pink',
  'plum',
  'purple',
  'violet',
  'indigo',
] as const;

export type AllowedColor = typeof ALLOWED_COLORS[number];

/**
 * Color mapping for design system colors to Radix UI colors
 */
export type ColorMapping = {
  primary: AllowedColor;
  secondary: AllowedColor;
  default: AllowedColor;
  success: AllowedColor;
  warning: AllowedColor;
  info: AllowedColor;
  error: AllowedColor;
  destructive: AllowedColor;
  orange: AllowedColor;
  blue: AllowedColor;
  red: AllowedColor;
  green: AllowedColor;
  gray: AllowedColor;
  teal: AllowedColor;
};

/**
 * Default color mappings for design system
 */
export const DEFAULT_COLOR_MAPPINGS: ColorMapping = {
  primary: 'indigo',
  secondary: 'gray',
  default: 'gray',
  success: 'jade',
  warning: 'amber',
  info: 'blue',
  error: 'crimson',
  destructive: 'ruby',
  orange: 'orange',
  blue: 'blue',
  red: 'red',
  green: 'green',
  gray: 'gray',
  teal: 'teal',
};

/**
 * Resolve design system color to Radix UI color
 *
 * @param color - Design system color name
 * @param accentColor - Current theme accent color (optional)
 * @param colorMappings - Custom color mappings (optional)
 * @returns Radix UI color name or undefined for special cases
 */
export function resolveColor<T extends string | undefined = string>(
  color: keyof ColorMapping | string,
  accentColor?: string,
  colorMappings: Partial<ColorMapping> = {}
): T {
  const mappings = { ...DEFAULT_COLOR_MAPPINGS, ...colorMappings };

  switch (color) {
    case 'primary':
      // Use accentColor if it's allowed, otherwise fallback
      return (ALLOWED_COLORS.includes(accentColor as AllowedColor)
        ? accentColor
        : PRIMARY_COLOR_FALLBACK) as T;

    case 'secondary':
    case 'default':
    case 'success':
    case 'warning':
    case 'info':
    case 'error':
    case 'destructive':
    case 'orange':
    case 'blue':
    case 'red':
    case 'green':
    case 'gray':
    case 'teal':
      return mappings[color as keyof ColorMapping] as T;

    default:
      return mappings.default as T;
  }
}

/**
 * Check if color is allowed in Radix UI theme
 */
export function isAllowedColor(color: string): color is AllowedColor {
  return ALLOWED_COLORS.includes(color as AllowedColor);
}

/**
 * Get primary color with fallback
 */
export function getPrimaryColor(accentColor?: string): AllowedColor {
  return isAllowedColor(accentColor || '')
    ? (accentColor as AllowedColor)
    : PRIMARY_COLOR_FALLBACK;
}
